/**
 * Automations catalog — every trigger, condition and action the builder
 * offers, defined declaratively:
 *
 *   id       stable key stored on the Automation (server matches on it)
 *   group    dropdown grouping header
 *   label    dropdown title (plain English, sentence case)
 *   blurb    dropdown one-liner under the title
 *   icon     small stroke icon (currentColor)
 *   defaults initial config when the step is added
 *   fields   setup-panel definition (sections → labeled controls)
 *   summary  (config) → the one-liner under the card title on the canvas
 *   english  (config) → the "In plain English" read-back (triggers)
 *
 * Adding a future trigger/action = adding an entry here. Nothing else.
 */
import React from "react";

/* ------------------------------ icons ------------------------------ */
const I = {
  trendDown: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" /><polyline points="17 18 23 18 23 12" />
    </svg>
  ),
  trendUp: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
    </svg>
  ),
  target: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" />
    </svg>
  ),
  bars: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  ),
  clock: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" />
    </svg>
  ),
  pause: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" /><line x1="8" y1="12" x2="16" y2="12" />
    </svg>
  ),
  rank: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="8 7 12 3 16 7" /><polyline points="8 17 12 21 16 17" />
    </svg>
  ),
  trophy: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0z" /><path d="M17 5h3v2a3 3 0 0 1-3 3M7 5H4v2a3 3 0 0 0 3 3" />
    </svg>
  ),
  repeat: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  ),
  alarm: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="13" r="8" /><path d="M12 9v4l2 2" /><path d="M5 3 2 6M22 6l-3-3" />
    </svg>
  ),
  calendarStart: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="3" y1="10" x2="21" y2="10" /><polygon points="10 14 15 16.5 10 19" />
    </svg>
  ),
  calendarEnd: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="3" y1="10" x2="21" y2="10" /><polyline points="9 16 11.5 18.5 16 14" />
    </svg>
  ),
  calendar: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  gift: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 12v9H4v-9" /><rect x="2" y="7" width="20" height="5" rx="1" /><line x1="12" y1="21" x2="12" y2="7" />
      <path d="M12 7S12 3 9 3a2 2 0 0 0 0 4z" /><path d="M12 7s0-4 3-4a2 2 0 0 1 0 4z" />
    </svg>
  ),
  ribbon: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="6" /><path d="M8.2 13.5 7 22l5-3 5 3-1.2-8.5" />
    </svg>
  ),
  userPlus: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" />
    </svg>
  ),
  userGear: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="7" r="4" /><path d="M2 21v-2a4 4 0 0 1 4-4h4" /><circle cx="18" cy="16" r="3" /><path d="M18 12.5v1M18 18.5v1M21 16h-1M16 16h-1" />
    </svg>
  ),
  columns: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="18" rx="1" /><rect x="14" y="3" width="7" height="11" rx="1" />
    </svg>
  ),
  filter: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="22 3 2 3 10 12.5 10 19 14 21 14 12.5 22 3" />
    </svg>
  ),
  bell: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  ),
  shield: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  users: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  userX: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="17" y1="8" x2="22" y2="13" /><line x1="22" y1="8" x2="17" y2="13" />
    </svg>
  ),
  star: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15 9 22 9 16.5 13.5 18.5 21 12 16.5 5.5 21 7.5 13.5 2 9 9 9 12 2" />
    </svg>
  ),
  note: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="13" y2="17" />
    </svg>
  ),
  task: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  ),
};

/* ---------------------------- shared bits ---------------------------- */

// Live values a message can drop in — the sender swaps them per recipient.
// `hint` shows as a tooltip on the insert button so {curly braces} read as
// "live value" rather than leftover code the first time someone sees one.
export const MESSAGE_TOKENS = [
  { key: "First name", hint: "The recipient's first name" },
  { key: "Points", hint: "Their current point total" },
  { key: "Benchmark", hint: "The benchmark or target this automation is comparing against" },
  { key: "Player", hint: "The full name of the player this automation is about" },
  { key: "Workspace", hint: "This automation's workspace name" },
];

// Stand-in values for the live preview — never real data, just something
// concrete so a message full of {tokens} is readable before it ever sends.
const MESSAGE_TOKEN_SAMPLES = {
  "First name": "Alex",
  Points: "4,200",
  Benchmark: "4,000",
  Player: "Alex Morgan",
  Workspace: "the team",
};

// Mirrors the server's applyTokens (channelDelivery.js) — a single pass over
// the original template so a sample value is never re-scanned for further
// token matches, and any token with no sample just renders as-is.
export const previewTokens = (template, workspaceName) => {
  const samples = workspaceName
    ? { ...MESSAGE_TOKEN_SAMPLES, Workspace: workspaceName }
    : MESSAGE_TOKEN_SAMPLES;
  return String(template || "").replace(/\{([^{}]+)\}/g, (match, key) =>
    key in samples ? samples[key] : match
  );
};

export const TIMES = ["7:00 AM", "8:00 AM", "9:00 AM", "10:00 AM", "12:00 PM", "3:00 PM", "5:00 PM", "6:00 PM"];

// Every "Notify …" action shares the same delivery setup: multi-channel
// (one alert, sent every way you pick), send timing, exact time, message.
const deliveryFields = (messagePlaceholder) => [
  {
    key: "channels", label: "Channels", type: "channels", section: "Delivery",
    options: ["SMS", "In-app", "Email"],
  },
  {
    key: "sendWhen", label: "When to send", type: "select", section: "Delivery",
    options: ["Immediately", "At a specific time", "After a delay"],
  },
  {
    key: "time", label: "Time", type: "select", section: "Delivery", options: TIMES,
    showIf: (cfg) => cfg.sendWhen === "At a specific time",
  },
  {
    key: "delay", label: "Delay", type: "select", section: "Delivery",
    options: ["1 hour", "3 hours", "1 day"],
    showIf: (cfg) => cfg.sendWhen === "After a delay",
  },
  {
    key: "message", label: "Message", type: "message", section: "Message",
    placeholder: messagePlaceholder,
  },
];

const deliveryDefaults = (message) => ({
  channels: ["SMS", "In-app"],
  sendWhen: "Immediately",
  time: "9:00 AM",
  delay: "1 hour",
  message,
});

// notify_player/notify_manager's default message depends on whether the
// trigger it's paired with is good news, bad news, or neither — a "hits a
// target" automation defaulting to "under the benchmark, needs a check-in"
// copy reads like a warning on an achievement. Every other trigger not
// listed here (new player, birthday, schedule triggers, etc.) falls back to
// "neutral", which has no {Benchmark} token since most of those have none.
const TRIGGER_SENTIMENT = {
  below_benchmark: "concern",
  below_benchmark_probation: "concern",
  player_inactive: "concern",
  hits_target: "win",
  above_benchmark: "win",
  reaches_first: "win",
};

const sentimentOf = (triggerType) => TRIGGER_SENTIMENT[triggerType] || "neutral";

const NOTIFY_PLAYER_MESSAGES = {
  concern: "Hi {First name} — you're at {Points}, under the {Benchmark}. Let's turn it around this week.",
  win: "Nice work, {First name} — you just hit {Points} points! 🎉",
  neutral: "Hi {First name} — quick update: you're now at {Points} points.",
};

const NOTIFY_MANAGER_MESSAGES = {
  concern: "Heads up — {Player} needs a check-in. They're at {Points}, under the {Benchmark}.",
  win: "Good news — {Player} just hit {Points} points! 🎉",
  neutral: "Update on {Player} — now at {Points} points.",
};

const notifyPlayerDefaults = (triggerType) => deliveryDefaults(NOTIFY_PLAYER_MESSAGES[sentimentOf(triggerType)]);
const notifyManagerDefaults = (triggerType) => deliveryDefaults(NOTIFY_MANAGER_MESSAGES[sentimentOf(triggerType)]);

// Every notify_* action with a fixed built-in target (player/manager/workspace/
// except) can ALSO ping specific extra people on top of that target — an
// "also notify" list, never a replacement for the built-in recipient.
// notify_one is exempt: picking the one person IS its whole job already.
const withExtraRecipients = (defaults) => (triggerType) => ({
  ...(typeof defaults === "function" ? defaults(triggerType) : defaults),
  extraRecipientIds: [],
});

const channelsPhrase = (cfg) =>
  Array.isArray(cfg.channels) && cfg.channels.length ? cfg.channels.join(" + ") : "no channel yet";

const DATE_ARRIVES_MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

// Mirrors the server's parseDateArrives (automationTriggers.js) so the
// builder can reject an unparseable date before save instead of silently
// saving an automation that will never fire.
export const isValidDateArrives = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return false;
  if (/^(\d{1,2})\s*\/\s*(\d{1,2})$/.test(raw)) return true;
  const named = raw.match(/^([A-Za-z]{3,})\.?\s+(\d{1,2})$/);
  if (!named) return false;
  return DATE_ARRIVES_MONTHS.includes(named[1].slice(0, 3).toLowerCase());
};

/* ------------------------------ triggers ------------------------------ */

export const TRIGGERS = [
  // Performance
  {
    id: "below_benchmark", group: "Performance", icon: I.trendDown,
    label: "A player falls below the benchmark", blurb: "points drop under the Yeti line",
    defaults: { benchmark: "Yeti benchmark" },
    fields: [
      { key: "benchmark", label: "Benchmark to watch", type: "select", section: "Trigger", options: ["Yeti benchmark", "Team average", "A custom points target"] },
      { key: "amount", label: "Points", type: "text", section: "Trigger", placeholder: "e.g. 4000", showIf: (cfg) => cfg.benchmark === "A custom points target" },
    ],
    summary: (cfg) => (cfg.benchmark === "A custom points target"
      ? `Starts when someone's points drop under ${cfg.amount || "…"} points.`
      : `Starts when someone's points drop under the ${(cfg.benchmark || "benchmark").toLowerCase()}.`),
    english: (cfg) => (cfg.benchmark === "A custom points target"
      ? `Fires the moment a player drops below ${cfg.amount || "…"} points.`
      : `Fires the moment a player drops below the ${(cfg.benchmark || "benchmark").toLowerCase()}.`),
  },
  {
    id: "above_benchmark", group: "Performance", icon: I.trendUp,
    label: "A player rises above the benchmark", blurb: "climbs back over the Yeti line",
    defaults: { benchmark: "Yeti benchmark" },
    fields: [
      { key: "benchmark", label: "Benchmark to watch", type: "select", section: "Trigger", options: ["Yeti benchmark", "Team average", "A custom points target"] },
      { key: "amount", label: "Points", type: "text", section: "Trigger", placeholder: "e.g. 4000", showIf: (cfg) => cfg.benchmark === "A custom points target" },
    ],
    summary: (cfg) => (cfg.benchmark === "A custom points target"
      ? `Starts when someone climbs back over ${cfg.amount || "…"} points.`
      : `Starts when someone climbs back over the ${(cfg.benchmark || "benchmark").toLowerCase()}.`),
    english: (cfg) => (cfg.benchmark === "A custom points target"
      ? `Fires the moment a player rises above ${cfg.amount || "…"} points.`
      : `Fires the moment a player rises above the ${(cfg.benchmark || "benchmark").toLowerCase()}.`),
  },
  {
    id: "hits_target", group: "Performance", icon: I.target,
    label: "A player hits a target", blurb: "reaches a goal or milestone",
    defaults: { target: "Their monthly goal" },
    fields: [
      { key: "target", label: "The target", type: "select", section: "Trigger", options: ["Their monthly goal", "A custom points target"] },
      { key: "amount", label: "Points", type: "text", section: "Trigger", placeholder: "e.g. 500", showIf: (cfg) => cfg.target === "A custom points target" },
    ],
    summary: (cfg) => (cfg.target === "A custom points target" ? `Starts when someone reaches ${cfg.amount || "…"} points.` : "Starts when someone reaches their monthly goal."),
    english: (cfg) => (cfg.target === "A custom points target" ? `Fires when a player reaches ${cfg.amount || "…"} points.` : "Fires when a player reaches their monthly goal."),
  },
  {
    id: "points_change", group: "Performance", icon: I.bars,
    label: "A player's points change", blurb: "any rise or fall in their score",
    defaults: { direction: "Any change" },
    fields: [
      { key: "direction", label: "Which changes", type: "select", section: "Trigger", options: ["Any change", "Only drops", "Only gains"] },
    ],
    summary: (cfg) => {
      if (cfg.direction === "Only drops") return "Starts whenever someone's points fall.";
      if (cfg.direction === "Only gains") return "Starts whenever someone's points rise.";
      return "Starts whenever someone's points move.";
    },
    english: (cfg) => `Fires on ${(cfg.direction || "any change").toLowerCase()} in a player's points.`,
  },
  {
    id: "below_benchmark_probation", group: "Performance", icon: I.clock,
    label: "Below the benchmark through probation", blurb: "probation-style monitoring (1st, 2nd month…)",
    defaults: { benchmark: "Yeti benchmark", window: "First 2 months", alertWhen: "when the 2nd month ends" },
    fields: [
      { key: "benchmark", label: "Benchmark to watch", type: "select", section: "Trigger", options: ["Yeti benchmark", "Team average", "A custom points target"] },
      { key: "amount", label: "Points", type: "text", section: "Trigger", placeholder: "e.g. 4000", showIf: (cfg) => cfg.benchmark === "A custom points target" },
      { key: "window", label: "Probation window", type: "select", section: "Trigger", options: ["1st month only", "2nd month only", "First 2 months", "First 3 months"] },
      { key: "alertWhen", label: "Send the alert", type: "select", section: "Trigger", options: ["as soon as they drop below", "when their 1st month ends", "when the 2nd month ends", "after probation ends"] },
    ],
    summary: (cfg) => `Watches their ${(cfg.window || "first months").toLowerCase()} of probation, then alerts ${cfg.alertWhen || ""}.`,
    english: (cfg) => `Watches a new player's ${(cfg.window || "first months").toLowerCase()} of probation and fires ${cfg.alertWhen || ""} if they're still below ${cfg.benchmark === "A custom points target" ? `${cfg.amount || "…"} points` : `the ${(cfg.benchmark || "benchmark").toLowerCase()}`}.`,
  },
  {
    id: "player_inactive", group: "Performance", icon: I.pause,
    label: "A player goes inactive", blurb: "no login for X days",
    defaults: { days: "7 days" },
    fields: [
      { key: "days", label: "Quiet for", type: "select", section: "Trigger", options: ["3 days", "5 days", "7 days", "14 days", "30 days"] },
    ],
    summary: (cfg) => `Starts when someone hasn't logged in for ${cfg.days || "a while"}.`,
    english: (cfg) => `Fires when a player has not logged in for ${cfg.days || "a while"}.`,
  },

  // Leaderboard
  {
    id: "rank_change", group: "Leaderboard", icon: I.rank,
    label: "A player ranks up or down", blurb: "moves position on the leaderboard",
    defaults: { direction: "Any move" },
    fields: [
      { key: "direction", label: "Which moves", type: "select", section: "Trigger", options: ["Any move", "Up only", "Down only"] },
    ],
    summary: (cfg) => {
      if (cfg.direction === "Up only") return "Starts when someone climbs the leaderboard.";
      if (cfg.direction === "Down only") return "Starts when someone slips down the leaderboard.";
      return "Starts when someone moves on the leaderboard.";
    },
    english: (cfg) => `Fires on ${(cfg.direction || "any move").toLowerCase()} on the leaderboard.`,
  },
  {
    id: "reaches_first", group: "Leaderboard", icon: I.trophy,
    label: "A player reaches #1", blurb: "tops the leaderboard",
    defaults: {},
    fields: [],
    summary: () => "Starts when someone takes the top spot.",
    english: () => "Fires when a player reaches #1 on the leaderboard.",
  },

  // Time & schedule
  {
    id: "on_schedule", group: "Time & schedule", icon: I.repeat,
    label: "On a schedule", blurb: "every day, week or month",
    defaults: { every: "Every day", time: "9:00 AM" },
    fields: [
      { key: "every", label: "Repeats", type: "select", section: "Trigger", options: ["Every day", "Every week", "Every month"] },
      { key: "time", label: "Time", type: "select", section: "Trigger", options: TIMES },
    ],
    summary: (cfg) => `Runs ${(cfg.every || "every day").toLowerCase()} at ${cfg.time || "9:00 AM"}.`,
    english: (cfg) => `Runs ${(cfg.every || "every day").toLowerCase()} at ${cfg.time || "9:00 AM"}.`,
  },
  {
    id: "at_time", group: "Time & schedule", icon: I.alarm,
    label: "At a specific time", blurb: "e.g. 9:00 AM each day",
    defaults: { time: "9:00 AM", days: "Every day" },
    fields: [
      { key: "time", label: "Time", type: "select", section: "Trigger", options: TIMES },
      { key: "days", label: "On", type: "select", section: "Trigger", options: ["Every day", "Weekdays only"] },
    ],
    summary: (cfg) => `Runs at ${cfg.time || "9:00 AM"}, ${(cfg.days || "every day").toLowerCase()}.`,
    english: (cfg) => `Runs at ${cfg.time || "9:00 AM"}, ${(cfg.days || "every day").toLowerCase()}.`,
  },
  {
    id: "period_start", group: "Time & schedule", icon: I.calendarStart,
    label: "Start of a period", blurb: "the month or week begins (fresh reset)",
    defaults: { period: "Month" },
    fields: [
      { key: "period", label: "Period", type: "select", section: "Trigger", options: ["Month", "Week"] },
    ],
    summary: (cfg) => `Runs when the ${(cfg.period || "month").toLowerCase()} begins.`,
    english: (cfg) => `Runs at the start of every ${(cfg.period || "month").toLowerCase()}.`,
  },
  {
    id: "period_end", group: "Time & schedule", icon: I.calendarEnd,
    label: "End of a period", blurb: "the month or week closes (wrap-up)",
    defaults: { period: "Month" },
    fields: [
      { key: "period", label: "Period", type: "select", section: "Trigger", options: ["Month", "Week"] },
    ],
    summary: (cfg) => `Runs when the ${(cfg.period || "month").toLowerCase()} closes.`,
    english: (cfg) => `Runs at the end of every ${(cfg.period || "month").toLowerCase()}.`,
  },
  {
    id: "date_arrives", group: "Time & schedule", icon: I.calendar,
    label: "A date arrives", blurb: "a specific date on a field",
    defaults: { date: "" },
    fields: [
      { key: "date", label: "Date", type: "text", section: "Trigger", placeholder: "e.g. Dec 1 or 12/1", hint: "Same date every year — month and day only, no year." },
    ],
    summary: (cfg) => (cfg.date ? `Runs on ${cfg.date}.` : "Runs on the date you pick."),
    english: (cfg) => (cfg.date ? `Runs on ${cfg.date}.` : "Runs on the date you pick."),
  },

  // Reminders
  {
    id: "birthday", group: "Reminders", icon: I.gift,
    label: "A birthday is coming up", blurb: "a set number of days before someone's birthday",
    defaults: { daysBefore: "2 days before" },
    fields: [
      { key: "daysBefore", label: "Heads-up", type: "select", section: "Trigger", options: ["On the day", "1 day before", "2 days before", "3 days before", "1 week before"] },
    ],
    summary: (cfg) => `Starts ${(cfg.daysBefore || "2 days before").toLowerCase()} someone's birthday.`,
    english: (cfg) => `Watches every player's birthday automatically and runs ${(cfg.daysBefore || "2 days before").toLowerCase()} each one — that player is who the next steps are about.`,
  },
  {
    id: "work_anniversary", group: "Reminders", icon: I.ribbon,
    label: "A work anniversary", blurb: "days before someone's hire date",
    defaults: { daysBefore: "On the day" },
    fields: [
      { key: "daysBefore", label: "Heads-up", type: "select", section: "Trigger", options: ["On the day", "1 day before", "2 days before", "1 week before"] },
    ],
    summary: (cfg) => `Starts ${(cfg.daysBefore || "on the day").toLowerCase()} of someone's work anniversary.`,
    english: (cfg) => `Fires ${(cfg.daysBefore || "on the day").toLowerCase()} of each player's work anniversary.`,
  },

  // People & workspace
  {
    id: "new_player", group: "People & workspace", icon: I.userPlus,
    label: "A new player joins", blurb: "when someone's added to a workspace",
    defaults: {},
    fields: [],
    summary: () => "Starts when a new player is added to this workspace.",
    english: () => "Fires when a new player joins the workspace.",
  },
  {
    id: "role_change", group: "People & workspace", icon: I.userGear,
    label: "A player's role changes", blurb: "promoted, reassigned or moved",
    defaults: {},
    fields: [],
    summary: () => "Starts when someone's role changes.",
    english: () => "Fires when a player's role changes.",
  },
  {
    id: "status_change", group: "People & workspace", icon: I.columns,
    label: "A status changes", blurb: "when a status or label is updated",
    defaults: {},
    fields: [],
    summary: () => "Starts when a status or label is updated.",
    english: () => "Fires when a status or label is updated.",
  },
];

/* ----------------------------- conditions ----------------------------- */

export const CONDITIONS = [
  {
    id: "in_probation", group: "Only if", icon: I.filter,
    label: "They're in probation", blurb: "only new players in their first months",
    defaults: { window: "First 2 months" },
    fields: [
      { key: "window", label: "Probation window", type: "select", section: "Condition", options: ["1st month only", "2nd month only", "First 2 months", "First 3 months"] },
    ],
    summary: (cfg) => `Only continues for players in their ${(cfg.window || "first months").toLowerCase()} of probation.`,
  },
  {
    id: "below_for_months", group: "Only if", icon: I.filter,
    label: "Still below after X months", blurb: "below the benchmark several months running",
    defaults: { months: "2 months" },
    fields: [
      { key: "months", label: "Months running", type: "select", section: "Condition", options: ["1 month", "2 months", "3 months"] },
    ],
    summary: (cfg) => `Only continues if they're below the benchmark ${cfg.months || "2 months"} running.`,
  },
  {
    id: "role_is", group: "Only if", icon: I.filter,
    label: "Their role is…", blurb: "limit to players or admins",
    defaults: { role: "Players" },
    fields: [
      { key: "role", label: "Role", type: "select", section: "Condition", options: ["Players", "Admins"] },
    ],
    summary: (cfg) => `Only continues for ${(cfg.role || "players").toLowerCase()}.`,
  },
];

/* ------------------------------ actions ------------------------------ */

export const ACTIONS = [
  // Notify someone — one alert, channels picked inside (any or all).
  {
    id: "notify_player", group: "Notify someone", icon: I.bell,
    groupNote: "One alert — pick the channels (SMS · in-app · email) inside. You can send it every way at once.",
    label: "Notify the player", blurb: "reach the player by SMS, in-app and/or email",
    recipient: "The player",
    recipientHint: "Whoever the trigger fired for — e.g. the player who dropped below.",
    extraRecipients: true,
    defaults: withExtraRecipients(notifyPlayerDefaults),
    fields: deliveryFields("Write the message the player receives…"),
    summary: (cfg) => `Reaches the player by ${channelsPhrase(cfg)} so they can't miss it.`,
  },
  {
    id: "notify_manager", group: "Notify someone", icon: I.shield,
    label: "Notify their manager", blurb: "reach the player's manager",
    recipient: "Their manager",
    recipientHint: "By default, every admin in the workspace. Pick someone to send it to just them.",
    managerPicker: true,
    defaults: (triggerType) => ({ ...notifyManagerDefaults(triggerType), managerId: "", managerName: "" }),
    fields: deliveryFields("Write the message the manager receives…"),
    summary: (cfg) => (cfg.managerName
      ? `Reaches ${cfg.managerName} by ${channelsPhrase(cfg)}.`
      : `Reaches every manager by ${channelsPhrase(cfg)}.`),
  },
  {
    id: "notify_one", group: "Notify someone", icon: I.userGear,
    label: "Notify a specific person", blurb: "pick exactly who gets it",
    recipientPicker: true,
    recipientHint: "Choose anyone in this workspace — not just the trigger's manager.",
    defaults: (triggerType) => ({ ...notifyManagerDefaults(triggerType), recipientId: "", recipientName: "" }),
    fields: deliveryFields("Write the message they receive…"),
    summary: (cfg) => (cfg.recipientName
      ? `Reaches ${cfg.recipientName} by ${channelsPhrase(cfg)}.`
      : `Reaches whoever you pick by ${channelsPhrase(cfg)}.`),
  },
  {
    id: "notify_workspace", group: "Notify someone", icon: I.users,
    label: "Notify a workspace", blurb: "everyone in a workspace",
    recipient: "Everyone in the workspace",
    recipientHint: "Every player in this automation's workspace.",
    extraRecipients: true,
    defaults: withExtraRecipients(deliveryDefaults("Team {Workspace} — quick update!")),
    fields: deliveryFields("Write the message everyone receives…"),
    summary: (cfg) => `Reaches everyone in the workspace by ${channelsPhrase(cfg)}.`,
  },
  {
    id: "notify_except", group: "Notify someone", icon: I.userX,
    label: "Notify everyone except one person", blurb: "great for birthdays & surprises",
    recipient: "Everyone except them",
    recipientHint: "“Them” = the person the trigger is about — for a birthday, the birthday player. Everyone else gets the message; they never see it.",
    extraRecipients: true,
    defaults: withExtraRecipients(deliveryDefaults("It's {Player}'s birthday in 2 days — don't spoil it! 🎂")),
    fields: deliveryFields("Write the message everyone (except them) receives…"),
    summary: (cfg) => `Reaches everyone except that person by ${channelsPhrase(cfg)}.`,
  },

  // Points & rewards
  {
    id: "give_points", group: "Points & rewards", icon: I.star,
    label: "Give points", blurb: "award bonus points",
    defaults: { amount: "50", kpiId: "", kpiName: "" },
    fields: [
      { key: "amount", label: "Points to give", type: "text", section: "Reward", placeholder: "e.g. 50" },
      { key: "kpiId", label: "Credit to metric", type: "kpi", section: "Reward" },
    ],
    summary: (cfg) => {
      const pts = cfg.amount || "…";
      return cfg.kpiName
        ? `Awards ${pts} points on “${cfg.kpiName}”.`
        : `Awards ${pts} bonus points.`;
    },
  },
  {
    id: "deduct_points", group: "Points & rewards", icon: I.pause,
    label: "Deduct points", blurb: "remove points",
    defaults: { amount: "25", kpiId: "", kpiName: "" },
    fields: [
      { key: "amount", label: "Points to remove", type: "text", section: "Reward", placeholder: "e.g. 25" },
      { key: "kpiId", label: "Deduct from metric", type: "kpi", section: "Reward" },
    ],
    summary: (cfg) => {
      const pts = cfg.amount || "…";
      return cfg.kpiName
        ? `Removes ${pts} points from “${cfg.kpiName}”.`
        : `Removes ${pts} points.`;
    },
  },
  {
    id: "award_badge", group: "Points & rewards", icon: I.ribbon,
    label: "Award a badge", blurb: "give a badge or trophy",
    defaults: { badgeId: "", badge: "" },
    fields: [
      { key: "badgeId", label: "Badge", type: "badge", section: "Reward" },
    ],
    summary: (cfg) => (cfg.badge ? `Awards the “${cfg.badge}” badge.` : "Awards a badge."),
  },

  // Record
  {
    id: "add_note", group: "Record", icon: I.note,
    label: "Add a note", blurb: "log a note on the player",
    defaults: { note: "" },
    fields: [
      { key: "note", label: "Note", type: "text", section: "Record", placeholder: "What should the note say?" },
    ],
    summary: (cfg) => (cfg.note ? `Logs a note: “${cfg.note}”` : "Logs a note on the player."),
  },
  {
    id: "create_task", group: "Record", icon: I.task,
    label: "Create a task", blurb: "add a follow-up to-do",
    defaults: { task: "" },
    fields: [
      { key: "task", label: "Task", type: "text", section: "Record", placeholder: "e.g. Book a 1-on-1" },
    ],
    summary: (cfg) => (cfg.task ? `Creates a task: “${cfg.task}”` : "Creates a follow-up task."),
  },
];

/* ------------------------------ lookups ------------------------------ */

const ALL = [...TRIGGERS, ...CONDITIONS, ...ACTIONS];

export const triggerById = (id) => TRIGGERS.find((t) => t.id === id) || null;
export const stepDefById = (id) => ALL.find((s) => s.id === id) || null;

export const TRIGGER_GROUPS = ["Performance", "Leaderboard", "Time & schedule", "Reminders", "People & workspace"];
export const STEP_GROUPS = ["Only if", "Notify someone", "Points & rewards", "Record"];

/** Auto-label for a step card: conditions read "Only if", the first action
 *  reads "Then do this", every action after it reads "And". */
export const stepTag = (steps, index) => {
  const step = steps[index];
  if (!step) return "";
  if (step.kind === "condition") return "Only if";
  const firstActionIdx = steps.findIndex((s) => s.kind === "action");
  return index === firstActionIdx ? "Then do this" : "And";
};

/** The list row's plain-English recipe line. */
export const recipeLine = (automation) => {
  const trig = triggerById(automation?.trigger?.type);
  const when = trig ? trig.label : "…";
  const parts = (automation?.steps || [])
    .filter((s) => s.kind === "action")
    .map((s) => {
      const def = stepDefById(s.type);
      return s.label || (def ? def.label : s.type);
    });
  return { when, actions: parts };
};
